// HtmlUrlEncode.jsx
import React from "react";

const HtmlUrlEncode = () => {
  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif", lineHeight: "1.6" }}>
      <h1>HTML URL Encoding - Comprehensive Notes</h1>

      {/* Introduction */}
      <section id="introduction">
        <h2>1. Introduction to URL Encoding</h2>
        <p>
          URL encoding, also known as <strong>percent-encoding</strong>, is used to encode special characters
          in URLs so that they can be transmitted over the internet safely. This ensures that URLs are valid
          and can be interpreted correctly by browsers and servers.
        </p>
      </section>

      {/* Why URL Encode */}
      <section id="why-url-encode">
        <h2>2. Why URL Encoding is Important</h2>
        <ul>
          <li>Spaces are not allowed in URLs → converted to <code>%20</code> or <code>+</code>.</li>
          <li>Special characters (<code>&amp;</code>, <code>=</code>, <code>?</code>) have reserved meanings.</li>
          <li>Non-ASCII characters must be encoded to ensure proper transmission.</li>
          <li>Prevents errors and security issues when sending data via GET requests.</li>
        </ul>
      </section>

      {/* How URL Encoding Works */}
      <section id="how-it-works">
        <h2>3. How URL Encoding Works</h2>
        <p>
          Each unsafe or reserved character is replaced with a <strong>percent sign (%)</strong> followed by
          its two-digit hexadecimal ASCII value.
        </p>
        <ul>
          <li>Space → <code>%20</code> or <code>+</code></li>
          <li>&lt; → <code>%3C</code></li>
          <li>&gt; → <code>%3E</code></li>
          <li># → <code>%23</code></li>
          <li>% → <code>%25</code></li>
        </ul>
      </section>

      {/* HTML Examples */}
      <section id="html-examples">
        <h2>4. HTML Examples</h2>
        <p>Basic URL with special characters:</p>
        <pre>
          <code>
{`<a href="https://example.com/search?query=HTML & CSS">Search HTML & CSS</a>`}
          </code>
        </pre>
        <p>Encoded URL version:</p>
        <pre>
          <code>
{`<a href="https://example.com/search?query=HTML%20%26%20CSS">Search HTML & CSS</a>`}
          </code>
        </pre>
      </section>

      {/* JavaScript Encoding */}
      <section id="javascript-encoding">
        <h2>5. JavaScript Methods for URL Encoding</h2>
        <ul>
          <li>
            <code>encodeURIComponent()</code> → Encodes a URI component (query string values):
            <pre>
              <code>{`const query = "HTML & CSS";
const encodedQuery = encodeURIComponent(query);
// Result: "HTML%20%26%20CSS"`}</code>
            </pre>
          </li>
          <li>
            <code>encodeURI()</code> → Encodes the entire URI (leaves URL syntax intact):
            <pre>
              <code>{`const url = "https://example.com/search?query=HTML & CSS";
const encodedUrl = encodeURI(url);
// Result: "https://example.com/search?query=HTML%20&%20CSS"`}</code>
            </pre>
          </li>
          <li>
            <code>decodeURIComponent()</code> → Decodes an encoded URI component:
            <pre>
              <code>{`decodeURIComponent("HTML%20%26%20CSS");
// Result: "HTML & CSS"`}</code>
            </pre>
          </li>
        </ul>
      </section>

      {/* Best Practices */}
      <section id="best-practices">
        <h2>6. Best Practices</h2>
        <ul>
          <li>Always encode user input in query strings to prevent errors and security issues.</li>
          <li>Use <code>encodeURIComponent</code> for query parameters, not the entire URL.</li>
          <li>Decode URLs when processing them on the server using <code>decodeURIComponent</code> or server-side equivalents.</li>
          <li>Avoid double-encoding (e.g., encoding an already encoded string).</li>
        </ul>
      </section>

      {/* Summary */}
      <section id="summary">
        <h2>7. Summary</h2>
        <p>
          URL encoding ensures safe and reliable transmission of URLs containing spaces, special characters,
          and non-ASCII characters. Always encode query strings and components properly using <code>encodeURIComponent</code>.
        </p>
      </section>
    </div>
  );
};

export default HtmlUrlEncode;
